/** The case's metrics as an editable table; a row without a label is left out when saving. */

import { Plus, Trash2 } from 'lucide-react';

import { metricInAfterOneMax, metricInBeforeOneMax, metricInLabelMax, metricInUnitOneMax, metricInValueOneMax } from '@/api/generated/schemas';
import { PROVENANCES, type Provenance } from '@/api/types/cases';
import type { FieldErrors } from '@/api/validate';
import { FieldError, fieldA11y } from '@/ui/FieldError';

import { metricRow, type MetricRow } from './caseForm';

const COLUMNS = [
  { key: 'label', title: 'Label', max: metricInLabelMax },
  { key: 'value', title: 'Value', max: metricInValueOneMax },
  { key: 'before', title: 'Before', max: metricInBeforeOneMax },
  { key: 'after', title: 'After', max: metricInAfterOneMax },
  { key: 'unit', title: 'Unit', max: metricInUnitOneMax },
] as const;

/** `errors` are keyed by row and column (`metrics.<row>.<column>`), as `caseErrors` gives them. */
export function MetricsEditor({ rows, onChange, errors = {} }: { rows: MetricRow[]; onChange: (rows: MetricRow[]) => void; errors?: FieldErrors }) {
  const patch = (key: string, change: Partial<MetricRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...change } : r)));
  return (
    <fieldset className="field">
      <legend className="label">Metrics</legend>
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="data compact metrics-editor" aria-label="Metrics">
            <thead>
              <tr>
                {COLUMNS.map((c) => <th key={c.key} scope="col">{c.title}</th>)}
                <th scope="col">Provenance</th>
                <th scope="col"><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  {COLUMNS.map((c) => {
                    const error = errors[`metrics.${i}.${c.key}`];
                    const id = `metric-${r.key}-${c.key}`;
                    return (
                      <td key={c.key}>
                        <input
                          id={id}
                          type="text"
                          maxLength={c.max}
                          aria-label={`Metric ${i + 1} ${c.title.toLowerCase()}`}
                          {...fieldA11y(id, error)}
                          value={r[c.key]}
                          onChange={(e) => patch(r.key, { [c.key]: e.target.value })}
                        />
                        <FieldError inputId={id} message={error} />
                      </td>
                    );
                  })}
                  <td>
                    <select aria-label={`Metric ${i + 1} provenance`} value={r.provenance} onChange={(e) => patch(r.key, { provenance: e.target.value as Provenance })}>
                      {PROVENANCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="btn btn-secondary btn-icon" aria-label={`Remove metric ${i + 1}`} onClick={() => onChange(rows.filter((x) => x.key !== r.key))}>
                      <Trash2 aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange([...rows, metricRow()])}>
          <Plus aria-hidden="true" /> Add metric
        </button>
      </div>
      <span className="hint">Client measured: the client measured it on a live case. Self measured: GFT measured it (a benchmark). Expected: a target, not yet measured.</span>
    </fieldset>
  );
}
